import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import { collection, onSnapshot, query, orderBy, addDoc, serverTimestamp, doc, limit, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as rtdbRef, onValue, set, serverTimestamp as rtdbServerTimestamp } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import ReactionBar from './ReactionBar';
import MarkdownRenderer from './MarkdownRenderer';
import HoverTooltip from './HoverTooltip';
import AiToolsButton from './AiToolsButton';
import AiReplyGenerator from './AiReplyGenerator';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import AIStarterMoodModal from './AIStarterMoodModal';
import AIStarterSuggestionsModal from './AIStarterSuggestionsModal';
import SharedMediaModal from './SharedMediaModal';
import UserProfilePopup from './UserProfilePopup';
import EmojiPicker from 'emoji-picker-react';
import MessageStatus from './MessageStatus';
import ContextMenu from './ContextMenu';
import ChatMessage from './ChatMessage';
import withVisionAura from './visions/withVisionAura';
import { useTranslation } from 'react-i18next';

const ProfilePicture = withVisionAura(({ src, alt, onClick, className, userId }) => (
    <img src={src} alt={alt} onClick={onClick} className={className} />
));

const UserListPanel = ({ allUsers, selectedChatUser, onSelectUser, unreadMessageCounts, isLoading, lastMessages, t, searchTerm, onSearchTermChange }) => {
    const { onlineStatus, appFunctions } = useAppContext();
    const [contextMenu, setContextMenu] = useState(null); // { x, y, user }
    const longPressTimer = useRef();

    const handleContextMenu = (e, user) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY, user });
    };

    const handleTouchStart = (e, user) => {
        longPressTimer.current = setTimeout(() => {
            handleContextMenu({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY, preventDefault: () => {}, stopPropagation: () => {} }, user);
        }, 500); // 500ms for long press
    };

    const handleTouchEndOrMove = () => {
        clearTimeout(longPressTimer.current);
    };

    const closeContextMenu = () => setContextMenu(null);

    const getRelativeTime = (timestamp) => {
        if (!timestamp) return '';
        const now = new Date();
        let messageDate;

        if (typeof timestamp.toDate === 'function') {
            messageDate = timestamp.toDate();
        } else {
            messageDate = new Date(timestamp);
        }

        const diffSeconds = Math.floor((now.getTime() - messageDate.getTime()) / 1000);

        if (diffSeconds < 60) return t('time.secondsAgo', { count: diffSeconds });
        if (diffSeconds < 3600) return t('time.minutesAgo', { count: Math.floor(diffSeconds / 60) });
        if (diffSeconds < 86400) return t('time.hoursAgo', { count: Math.floor(diffSeconds / 3600) });
        if (diffSeconds < 604800) return t('time.daysAgo', { count: Math.floor(diffSeconds / 86400) });
        return messageDate.toLocaleDateString();
    };

    const togglePin = async (user) => {
        const toggleChatPinStatus = httpsCallable(appFunctions, 'toggleChatPinStatus');
        try {
            await toggleChatPinStatus({ chatPartnerId: user.id });
            // The UI will update automatically via the Firestore listener
        } catch (error) {
            console.error("Failed to toggle pin status:", error);
        }
    };

    const contextMenuItems = contextMenu ? [
        { label: contextMenu.user.isPinned ? t('messagesPage.userList.unpin') : t('messagesPage.userList.pin'), icon: LucideIcons.Pin, onClick: () => togglePin(contextMenu.user) },
        { label: t('messagesPage.userList.markUnread'), icon: LucideIcons.Mail, onClick: () => console.log('Marking unread...') },
        { label: t('messagesPage.userList.archive'), icon: LucideIcons.Archive, onClick: () => console.log('Archiving...') },
        { label: t('messagesPage.userList.block'), icon: LucideIcons.UserX, onClick: () => console.log('Blocking...') },
    ] : [];

    return (
        <div className="w-full flex flex-col h-full bg-gray-900 overflow-hidden">
            {contextMenu && <ContextMenu x={contextMenu.x} y={contextMenu.y} isOpen={!!contextMenu} onClose={closeContextMenu} items={contextMenuItems} />}
            <div className="p-4 border-b border-gray-800 backdrop-blur-md bg-gray-900/80 sticky top-0 z-10">
                <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-white font-sans truncate">{t('messagesPage.userList.title')}</h2>
                <div className="relative mt-2">
                    <input 
                        type="text"
                        placeholder={t('messagesPage.userList.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        className="w-full bg-gray-900/50 rounded-full py-2 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                    <LucideIcons.Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    {searchTerm && (
                        <button onClick={() => onSearchTermChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                            <LucideIcons.X size={20} />
                        </button>
                    )}
                </div>
            </div>
            {isLoading ? (
                <div className="flex-grow flex items-center justify-center">
                    <LoadingSpinner message={t('messagesPage.userList.loading')} />
                </div>
            ) : (
                <div className="flex-grow overflow-y-auto custom-scrollbar">
                    {allUsers.length > 0 ? (
                        allUsers.map(u => {
                            const status = onlineStatus[u.id];
                            const isOnline = status?.state === 'online';
                            const lastMessage = lastMessages[u.id];

                            return (
                                <div 
                                    key={u.id} 
                                    onContextMenu={(e) => handleContextMenu(e, u)}
                                    onTouchStart={(e) => handleTouchStart(e, u)}
                                    onTouchEnd={handleTouchEndOrMove}
                                    onTouchMove={handleTouchEndOrMove}
                                    onTouchCancel={handleTouchEndOrMove}
                                >
                                    <button
                                        onClick={() => onSelectUser(u.id, u.displayName)}
                                        className={`w-full text-left p-3 flex items-center gap-4 transition-all duration-200 ease-in-out border-b border-white/5 ${selectedChatUser?.id === u.id ? 'bg-indigo-500/20 shadow-[inset_4px_0_0_0_rgba(99,102,241,1)]' : 'hover:bg-white/5'} ${u.isPinned ? 'bg-yellow-500/5' : ''}`}
                                    >
                                        {u.isPinned && (
                                            <LucideIcons.Pin size={16} className="text-yellow-400 flex-shrink-0" />
                                        )}
                                        <div className="relative flex-shrink-0">
                                            <ProfilePicture src={u.photoURL || "https://placehold.co/48x48/AEC6CF/FFFFFF?text=U"} alt={u.displayName} className="w-12 h-12 rounded-full object-cover" userId={u.id} />
                                            {isOnline && (
                                                <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-400 ring-2 ring-gray-800" title={t('messagesPage.userList.online')} />
                                            )}
                                        </div>
                                        <div className="flex-grow truncate">
                                            <div className="flex justify-between items-baseline">
                                                <p className="font-semibold text-base text-white truncate">{u.displayName}</p>
                                                {lastMessage?.timestamp && (
                                                    <p className="text-xs text-gray-400 flex-shrink-0 ml-2">{getRelativeTime(lastMessage.timestamp)}</p>
                                                )}
                                            </div>
                                            <p className={`text-sm truncate ${unreadMessageCounts[u.id] > 0 ? 'text-white font-bold' : 'text-gray-400'}`}>
                                                {lastMessage?.content || (isOnline ? t('messagesPage.userList.online') : t('messagesPage.userList.offline'))}
                                            </p>
                                        </div>
                                        {unreadMessageCounts[u.id] > 0 && (
                                            <div className="bg-sky-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0" title={t('messagesPage.userList.unread', { count: unreadMessageCounts[u.id] })}>
                                                {unreadMessageCounts[u.id]}
                                            </div>
                                        )}
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        <p className="text-center text-gray-400 p-6">{t('messagesPage.userList.noMatches')}</p>
                    )}
                </div>
            )}
        </div>
    );
};

const addDateSeparators = (messages) => {
    const messagesWithSeparators = [];
    let lastDate = null;

    messages.forEach(message => {
        if (message.timestamp) {
            const messageDateObj = typeof message.timestamp.toDate === 'function' ? message.timestamp.toDate() : new Date(message.timestamp);
            const messageDate = messageDateObj.toLocaleDateString();
            if (messageDate !== lastDate) {
                messagesWithSeparators.push({ type: 'separator', date: messageDateObj });
                lastDate = messageDate;
            }
        }
        messagesWithSeparators.push(message);
    });

    return messagesWithSeparators;
};

function ChatPanel({ selectedChatUser, onBackToUsers, t }) {
    const { userId, db, rtdb, storage, appFunctions, userProfiles, setMessage, onlineStatus, appId, handlePageChange } = useAppContext();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isGeneratingStarter, setIsGeneratingStarter] = useState(false);
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [syncScore, setSyncScore] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [selectedEmoji, setSelectedEmoji] = useState(null);
    const [replyingToMessage, setReplyingToMessage] = useState(null);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const [aiModalContent, setAiModalContent] = useState(null);
    const [aiModalTitle, setAiModalTitle] = useState('');
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [showMoodModal, setShowMoodModal] = useState(false);
    const [starterSuggestions, setStarterSuggestions] = useState([]);
    const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
    const [showSharedMediaModal, setShowSharedMediaModal] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [showProfilePopup, setShowProfilePopup] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const messageListRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };
            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error("Error starting recording:", error);
            setMessage(t('messagesPage.chat.micDenied'));
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                audioChunksRef.current = [];

                // Upload and send the audio message
                const filePath = `private_chats/${chatId}/audio/${Date.now()}.webm`;
                const fileRef = storageRef(storage, filePath);
                const uploadTask = uploadBytesResumable(fileRef, audioBlob);

                try {
                    await uploadTask;
                    const audioUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    
                    const messageData = {
                        from: userId,
                        to: selectedChatUser.id,
                        audioUrl: audioUrl,
                        timestamp: serverTimestamp(),
                        read: false,
                        status: 'sent',
                    };
                    await addDoc(collection(db, `artifacts/${appId}/private_chats/${chatId}/messages`), messageData);

                } catch (error) {
                    console.error("Error uploading audio:", error);
                    setMessage(t('messagesPage.chat.audioSendFailed'));
                }
            };
        }
    };

    const handleImageSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setImagePreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleCancelImage = () => {
        setImageFile(null);
        setImagePreviewUrl(null);
        if(fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = messageListRef.current;
        // Show button if user has scrolled up more than a certain amount (e.g., 300px)
        if (scrollHeight - scrollTop > clientHeight + 300) {
            setShowScrollToBottom(true);
        } else {
            setShowScrollToBottom(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const recipientProfile = useMemo(() => userProfiles.find(u => u.id === selectedChatUser.id), [userProfiles, selectedChatUser]);
    const messagesWithSeparators = useMemo(() => addDateSeparators(messages), [messages]);
    const status = onlineStatus[selectedChatUser.id];
    const isOnline = status?.state === 'online';

    const chatId = useMemo(() => {
        if (!userId || !selectedChatUser) return null;
        return [userId, selectedChatUser.id].sort().join('_');
    }, [userId, selectedChatUser]);

    const handleUpdateStatus = useCallback(async (messageId, status) => {
        const updateMessageStatus = httpsCallable(appFunctions, 'updateMessageStatus');
        try {
            await updateMessageStatus({ chatId, messageId, status });
        } catch (error) {
            console.error(`Failed to update status to ${status}:`, error);
        }
    }, [appFunctions, chatId]);

    const handleEditMessage = useCallback(async (messageId, newContent) => {
        const editPrivateMessage = httpsCallable(appFunctions, 'editPrivateMessage');
        try {
            await editPrivateMessage({ chatId, messageId, newContent });
        } catch (error) {
            console.error("Failed to edit message:", error);
            setMessage(t('messagesPage.chat.editFailed', { message: error.message }));
        }
    }, [appFunctions, chatId, setMessage, t]);

    const handleSendMessage = useCallback(async (e) => {
        e.preventDefault();
        if ((!newMessage.trim() && !imageFile) || !selectedChatUser) return;

        const tempId = `temp_${Date.now()}`;
        const tempMessageContent = newMessage;
        const tempReplyingTo = replyingToMessage;
        const tempImageFile = imageFile;
        const tempImagePreviewUrl = imagePreviewUrl;

        setNewMessage('');
        setReplyingToMessage(null);
        setImageFile(null);
        setImagePreviewUrl(null);

        const optimisticMessage = {
            id: tempId,
            from: userId,
            to: selectedChatUser.id,
            content: tempMessageContent.trim(),
            timestamp: new Date(),
            status: 'sending',
            imageUrl: tempImagePreviewUrl,
            replyingTo: tempReplyingTo,
        };

        setMessages(prev => [...prev, optimisticMessage]);

        try {
            let imageUrl = '';
            if (tempImageFile) {
                const chatId = [userId, selectedChatUser.id].sort().join('_');
                const filePath = `private_chats/${chatId}/${Date.now()}_${tempImageFile.name}`;
                const fileRef = storageRef(storage, filePath);
                const uploadTask = uploadBytesResumable(fileRef, tempImageFile);
                await uploadTask;
                imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
            }

            const sendPrivateMessage = httpsCallable(appFunctions, 'sendPrivateMessage');
            const result = await sendPrivateMessage({
                recipientId: selectedChatUser.id,
                content: tempMessageContent.trim(),
                imageUrl: imageUrl || null,
                replyingTo: tempReplyingTo ? {
                    messageId: tempReplyingTo.id,
                    content: tempReplyingTo.content,
                    from: tempReplyingTo.from,
                } : null,
            });

            setMessages(prev => prev.map(msg => 
                msg.id === tempId ? { ...msg, id: result.data.messageId, status: 'sent' } : msg
            ));

        } catch (error) {
            console.error("Error sending message:", error);
            setMessage(t('messagesPage.chat.sendFailed', { message: error.message }));
            setMessages(prev => prev.map(msg => 
                msg.id === tempId ? { ...msg, status: 'failed' } : msg
            ));
        }
    }, [newMessage, imageFile, imagePreviewUrl, selectedChatUser, userId, storage, appFunctions, setMessage, replyingToMessage, t]);

    const handleSummarizeChat = async () => {
        const summarizeChat = httpsCallable(appFunctions, 'summarizeChat');
        try {
            const result = await summarizeChat({ chatId });
            setAiModalTitle(t('messagesPage.chat.summaryTitle'));
            setAiModalContent(result.data.summary);
        } catch (error) {
            console.error("Failed to summarize chat:", error);
            setMessage(t('messagesPage.chat.summaryFailed', { message: error.message }));
        }
    };

    useEffect(() => {
        if (searchTerm) {
            const results = messages.filter(msg => msg.content && msg.content.toLowerCase().includes(searchTerm.toLowerCase()));
            setSearchResults(results);
            setCurrentResultIndex(0);
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, messages]);

    // Typing indicator logic
    useEffect(() => {
        if (!chatId || !rtdb) return;

        const typingRef = rtdbRef(rtdb, `typing/${chatId}/${selectedChatUser.id}`);
        const unsubscribe = onValue(typingRef, (snapshot) => {
            setIsTyping(snapshot.val() === true);
        });

        return () => unsubscribe();
    }, [chatId, rtdb, selectedChatUser.id]);

    const handleTyping = (e) => {
        setNewMessage(e.target.value);

        if (!chatId || !rtdb) return;

        const userTypingRef = rtdbRef(rtdb, `typing/${chatId}/${userId}`);
        set(userTypingRef, true);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            set(userTypingRef, false);
        }, 3000); // 3-second timeout
    };

    const processedMessageIdsRef = useRef(new Set());

    useEffect(() => {
        if (!chatId || messages.length === 0) return;

        console.log('useEffect for updateStatuses triggered. ChatId:', chatId, 'Messages count:', messages.length);

        const updateStatuses = async () => {
            const updates = [];
            messages.forEach(msg => {
                // Only add to updates if it's from another user, status is 'sent', and we haven't processed it yet
                if (msg.from !== userId && msg.status === 'sent' && !processedMessageIdsRef.current.has(msg.id)) {
                    updates.push({ messageId: msg.id, status: 'delivered' });
                }
            });

            if (updates.length > 0) {
                const batchUpdateMessageStatus = httpsCallable(appFunctions, 'batchUpdateMessageStatus');
                console.log('Calling batchUpdateMessageStatus for', updates.length, 'messages.');
                batchUpdateMessageStatus({ chatId, updates })
                    .then(() => {
                        // Mark messages as processed after attempting the call
                        updates.forEach(update => processedMessageIdsRef.current.add(update.messageId));
                    })
                    .catch(err => {
                        console.error("Failed to batch update status to delivered:", err);
                        // Optionally, you might want to remove from processedMessageIdsRef if the call truly failed
                        // to allow retries, but for now, let's keep it simple to break the loop.
                    });
            }
        };

        updateStatuses();
    }, [messages, chatId, userId, appFunctions]);

    useEffect(() => {
        if (selectedChatUser?.id) {
            const markChatAsRead = httpsCallable(appFunctions, 'markChatAsRead');
            markChatAsRead({ chatPartnerId: selectedChatUser.id })
                .catch(err => console.error("Failed to mark chat as read:", err));
        }
    }, [selectedChatUser, appFunctions]);

    useEffect(() => {
        if (!selectedChatUser) return;
        const chatId = [userId, selectedChatUser.id].sort().join('_');
        const q = query(collection(db, `artifacts/${appId}/private_chats/${chatId}/messages`), orderBy("timestamp", "asc"));
        const unsubMessages = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const chatRef = doc(db, `artifacts/${appId}/private_chats/${chatId}`);
        const unsubChat = onSnapshot(chatRef, (docSnap) => {
            setSyncScore(docSnap.exists() ? docSnap.data().syncScore || 0 : 0);
        });

        return () => { unsubMessages(); unsubChat(); };
    }, [selectedChatUser, userId, db, appId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleAiReply = (reply) => {
        setNewMessage(reply);
    };

    const handleAnalysisComplete = (toolType, data) => {
        let title = "AI Analysis";
        let content = "";

        switch (toolType) {
            case 'GET_AURA_INSIGHT':
                title = data.colorName || "Aura Insight";
                content = `**Mood:** ${data.mood}\n\n**Themes:** ${data.themes.join(', ')}\n\n**Affirmation:** "${data.affirmation}"`;
                break;
            case 'GET_VIBE':
                title = "Conversation Vibe";
                content = data.text;
                break;
            default:
                title = toolType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                content = data.text;
        }

        setAiModalTitle(title);
        setAiModalContent(content);
    };

    const handleRetryMessage = useCallback(async (failedMessage) => {
        // Remove the failed message from the list
        setMessages(prev => prev.filter(msg => msg.id !== failedMessage.id));
        // Essentially re-sending the message
        // A more robust implementation might not require re-uploading images
        handleSendMessage({ preventDefault: () => {} }); // Call with a mock event
    }, [handleSendMessage]);

    const handleGenerateStarter = async (mood) => {
        if (!mood) {
            setShowMoodModal(true);
            return;
        }
        setShowMoodModal(false);
        setIsGeneratingStarter(true);
        const generateConversationStarter = httpsCallable(appFunctions, 'generateConversationStarter');
        try {
            const result = await generateConversationStarter({ recipientId: selectedChatUser.id, mood });
            setStarterSuggestions(result.data.starters);
            setShowSuggestionsModal(true);
        } catch (error) {
            console.error("Error generating starter:", error);
            setMessage(t('messagesPage.chat.starterFailed', { message: error.message }));
        } finally {
            setIsGeneratingStarter(false);
        }
    };

  const updateReaction = httpsCallable(appFunctions, 'updateReaction');
  const handleReactToMessage = async (chatId, messageId, emoji) => {
    const allEmojis = ['❤️', '😂', '👍', '😢', '🔥']; // Emojis available in ReactionBar

    // Optimistic update
    const originalMessages = [...messages]; // Store current messages for potential revert
    setMessages(prevMessages => {
        return prevMessages.map(msg => {
            if (msg.id === messageId) {
                const newReactions = { ...(msg.reactions || {}) };
                let userPreviousReaction = null;

                // Find if user has reacted with any emoji before
                for (const e of allEmojis) {
                    if (newReactions[e] && newReactions[e].includes(userId)) {
                        userPreviousReaction = e;
                        break;
                    }
                }

                if (userPreviousReaction && userPreviousReaction !== emoji) {
                    // User reacted with a different emoji previously, remove old reaction
                    newReactions[userPreviousReaction] = newReactions[userPreviousReaction].filter(id => id !== userId);
                    if (newReactions[userPreviousReaction].length === 0) {
                        delete newReactions[userPreviousReaction];
                    }
                }

                if (userPreviousReaction === emoji) {
                    // User is un-reacting with the same emoji
                    newReactions[emoji] = newReactions[emoji].filter(id => id !== userId);
                    if (newReactions[emoji].length === 0) {
                        delete newReactions[emoji];
                    }
                } else {
                    // User is reacting with a new emoji
                    newReactions[emoji] = [...(newReactions[emoji] || []), userId];
                }
                return { ...msg, reactions: newReactions };
            }
            return msg;
        });
    });

    setSelectedEmoji(null); // Clear selected emoji after reacting
    setShowEmojiPicker(false); // Close emoji picker

    try {
      if (!chatId || !messageId || !emoji) {
        console.error("Missing parameters for reacting to message.");
        // Revert optimistic update if parameters are missing
        setMessages(originalMessages);
        return;
      }

      const fullPath = `artifacts/${appId}/private_chats/${chatId}/messages/${messageId}`;

      // Call the Cloud Function to record the reaction
      await updateReaction({
        entryId: chatId,
        reaction: emoji,
        contentType: 'message',
        fullPath: fullPath,
      });
    } catch (error) {
      console.error("Error reacting to message:", error);
      setMessage(t('messagesPage.chat.reactError', { message: error.message }));
      // Revert optimistic update on failure
      setMessages(originalMessages);
    }
  };


    const handleSendGift = async (amount, giftMessage) => {
        console.log('handleSendGift called with amount:', amount, 'message:', giftMessage);
        console.log('Setting showGiftModal to false from handleSendGift');
        setShowGiftModal(false);
        if (!amount || amount <= 0) {
            setMessage(t('messagesPage.chat.giftAmountError'));
            return;
        }
        const giftEchoes = httpsCallable(appFunctions, 'giftEchoes');
        try {
            await giftEchoes({
                recipientId: selectedChatUser.id, amount: Number(amount), message: giftMessage
            });
            setMessage(t('messagesPage.chat.giftSuccess'));
        } catch (error) {
            console.error("Error sending gift:", error);
            setMessage(t('messagesPage.chat.giftFailed', { message: error.message }));
        }
    };

    const GiftModal = () => {
        console.log('GiftModal rendered');
        const [amount, setAmount] = useState(10);
        const [giftMessage, setGiftMessage] = useState('');
        useEffect(() => {
            console.log('GiftModal mounted');
            return () => console.log('GiftModal unmounted');
        }, []);
        return (
            <div className="modal-overlay">
                <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-sm w-full text-white relative">
                    <h3 className="text-xl font-bold mb-4 text-yellow-300 font-sans">{t('messagesPage.chat.giftModal.title')}</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold mb-2 text-gray-300">{t('messagesPage.chat.giftModal.amount')}</label>
                            <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} min="1" className="shadow appearance-none border rounded-lg w-full py-2 px-3 bg-gray-900 text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold mb-2 text-gray-300">{t('messagesPage.chat.giftModal.message')}</label>
                            <input type="text" value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} maxLength="50" placeholder={t('messagesPage.chat.giftModal.placeholder')} className="shadow appearance-none border rounded-lg w-full py-2 px-3 bg-gray-900 text-white" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                        <button onClick={() => { console.log('Setting showGiftModal to false from GiftModal Cancel'); setShowGiftModal(false); }} className="px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700">{t('common.cancel')}</button>
                        <button onClick={() => handleSendGift(amount, giftMessage)} className="px-4 py-2 bg-yellow-500 text-black font-bold rounded-md hover:bg-yellow-400">{t('messagesPage.chat.giftModal.send')}</button>
                    </div>
                </div>
            </div>
        );
    };

    const HarmonyMeter = () => {
        const glowIntensity = syncScore / 100;
        const glowStyle = {
            boxShadow: `0 0 ${glowIntensity * 15}px rgba(236, 72, 153, ${glowIntensity * 0.8}), 0 0 ${glowIntensity * 8}px rgba(168, 85, 247, ${glowIntensity * 0.6})`,
        };

        return (
            <div className="w-full bg-gray-700/50 rounded-full h-1">
                <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-1 rounded-full transition-all duration-500" 
                    style={{ width: `${syncScore}%`, ...glowStyle }}
                ></div>
            </div>
        );
    };

    return (
        <div className="w-full flex flex-col h-full bg-white dark:bg-gray-900 overflow-hidden">
            <UserProfilePopup 
                isOpen={showProfilePopup} 
                onClose={() => setShowProfilePopup(false)} 
                user={recipientProfile}
                syncScore={syncScore}
                onNavigateToProfile={() => handlePageChange('viewingProfile', { userId: recipientProfile.id })}
            />
            <SharedMediaModal isOpen={showSharedMediaModal} onClose={() => setShowSharedMediaModal(false)} chatId={chatId} />
            <AIStarterMoodModal isOpen={showMoodModal} onClose={() => setShowMoodModal(false)} onSelectMood={handleGenerateStarter} />
            <AIStarterSuggestionsModal 
                isOpen={showSuggestionsModal} 
                onClose={() => setShowSuggestionsModal(false)} 
                suggestions={starterSuggestions}
                onSelectSuggestion={(suggestion) => {
                    setNewMessage(suggestion);
                    setShowSuggestionsModal(false);
                }}
            />
            {aiModalContent && <AIGeneratedContentModal title={aiModalTitle} content={aiModalContent} onClose={() => setAiModalContent(null)} />}
            {showGiftModal && <GiftModal />}
            <div className="relative flex items-center p-4 border-b border-white/10 gap-4 flex-shrink-0">
                {isSearching ? (
                    <div className="flex-grow flex items-center gap-2">
                        <input
                            type="text"
                            placeholder={t('messagesPage.chat.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 bg-gray-900/50 rounded-full py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-400">{searchResults.length > 0 ? t('messagesPage.chat.searchResults', { current: currentResultIndex + 1, total: searchResults.length }) : '0/0'}</span>
                        <button disabled={searchResults.length === 0} onClick={() => setCurrentResultIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1))} className="p-1 rounded-full hover:bg-white/10 disabled:opacity-50">
                            <LucideIcons.ChevronUp size={20} />
                        </button>
                        <button disabled={searchResults.length === 0} onClick={() => setCurrentResultIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0))} className="p-1 rounded-full hover:bg-white/10 disabled:opacity-50">
                            <LucideIcons.ChevronDown size={20} />
                        </button>
                        <button onClick={() => { setIsSearching(false); setSearchTerm(''); }} className="p-1 rounded-full hover:bg-white/10">
                            <LucideIcons.X size={20} />
                        </button>
                    </div>
                ) : (
                    <>
                        <button onClick={onBackToUsers} className="p-2 rounded-full hover:bg-white/10 md:hidden">
                            <LucideIcons.ArrowLeft size={20} className="text-white" />
                        </button>
                        <button onClick={() => setShowProfilePopup(true)} className="flex items-center gap-4 text-left">
                            <div className="relative flex-shrink-0">
                                <img src={recipientProfile?.photoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} alt={selectedChatUser.displayName} className="w-10 h-10 rounded-full object-cover" />
                                {isOnline && (
                                    <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-gray-800" />
                                )}
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">{selectedChatUser.displayName}</h2>
                                <p className={`text-xs font-semibold ${isOnline ? 'text-green-400' : 'text-gray-400'}`}>
                                    {isTyping ? <span className="text-blue-400">{t('messagesPage.chat.typing')}</span> : (isOnline ? t('messagesPage.userList.online') : t('messagesPage.userList.offline'))}
                                </p>
                            </div>
                        </button>
                        <div className="flex-grow" />
                        <HoverTooltip text={t('messagesPage.chat.searchTooltip')}>
                            <button onClick={() => setIsSearching(true)} className="p-2 rounded-full hover:bg-white/10" aria-label={t('messagesPage.chat.searchTooltip')}>
                                <LucideIcons.Search size={20} className="text-white" />
                            </button>
                        </HoverTooltip>
                        <HoverTooltip text={t('messagesPage.chat.mediaTooltip')}>
                            <button onClick={() => setShowSharedMediaModal(true)} className="p-2 rounded-full hover:bg-white/10" aria-label={t('messagesPage.chat.mediaTooltip')}>
                                <LucideIcons.Image size={20} className="text-white" />
                            </button>
                        </HoverTooltip>
                        <HoverTooltip text={t('messagesPage.chat.summaryTooltip')}>
                            <button onClick={handleSummarizeChat} className="p-2 rounded-full hover:bg-white/10" aria-label={t('messagesPage.chat.summaryTooltip')}>
                                <LucideIcons.BookText size={20} className="text-white" />
                            </button>
                        </HoverTooltip>
                    </>
                )}
            </div>
            <div ref={messageListRef} onScroll={handleScroll} className="flex-1 overflow-y-auto custom-scrollbar min-h-0 relative">
                {showScrollToBottom && (
                    <button 
                        onClick={scrollToBottom} 
                        className="absolute bottom-4 right-4 bg-blue-600 text-white rounded-full p-2 shadow-lg hover:bg-blue-700 transition-opacity animate-bounce"
                    >
                        <LucideIcons.ArrowDown size={24} />
                    </button>
                )}
                <div className="p-6 space-y-2">
                    {messagesWithSeparators.map((item, index) => {
                        if (item.type === 'separator') {
                            return (
                                <div key={`separator-${index}`} className="text-center my-4">
                                    <span className="bg-gray-700 text-gray-300 text-xs font-semibold px-3 py-1 rounded-full">
                                        {new Date(item.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </span>
                                </div>
                            );
                        }
                        const msg = item;
                        return (
                            <ChatMessage
                                key={msg.id}
                                chatId={chatId}
                                msg={msg}
                                prevMessage={messagesWithSeparators[index - 1]}
                                isUser={msg.from === userId}
                                recipientProfile={recipientProfile}
                                onReact={handleReactToMessage}
                                onReply={setReplyingToMessage}
                                onAnalysisComplete={handleAnalysisComplete}
                                onAiReply={handleAiReply}
                                                            onUpdateStatus={handleUpdateStatus}
                                                            onEdit={handleEditMessage}
                                                            onRetry={() => handleRetryMessage(msg)}
                                                            searchTerm={searchTerm}
                                                        />                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
            </div>
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-800 bg-gray-900/90 backdrop-blur-md flex flex-col gap-2 flex-shrink-0 relative z-10">
                {imagePreviewUrl && (
                    <div className="relative w-24 h-24 self-start ml-2 mb-2">
                        <img src={imagePreviewUrl} alt="Preview" className="w-full h-full object-cover rounded-xl shadow-md border border-gray-700" />
                        <button onClick={handleCancelImage} className="absolute -top-2 -right-2 bg-gray-800 rounded-full text-gray-300 hover:text-white p-1 border border-gray-600 shadow-sm transition-colors">
                            <LucideIcons.X size={14} />
                        </button>
                    </div>
                )}
                {replyingToMessage && (
                    <div className="bg-gray-800/80 backdrop-blur-sm p-3 rounded-xl border border-gray-700 text-sm text-gray-300 relative ml-2 mr-2 mb-2 shadow-inner">
                        <p className="font-bold text-blue-400 text-xs mb-1 uppercase tracking-wide">{replyingToMessage.from === userId ? t('messagesPage.chat.replyingToYou') : t('messagesPage.chat.replyingToOther', { name: recipientProfile?.displayName })}</p>
                        <p className="truncate italic text-[13px]">"{replyingToMessage.content}"</p>
                        <button onClick={() => setReplyingToMessage(null)} className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors">
                            <LucideIcons.X size={16} />
                        </button>
                    </div>
                )}
                <div className="flex items-center gap-1.5 relative bg-gray-800/80 rounded-full p-1.5 shadow-inner border border-gray-700/50 focus-within:ring-2 focus-within:ring-indigo-500/50 transition-all">
                    {showEmojiPicker && (
                        <div className="absolute bottom-14 left-0 z-20 shadow-2xl rounded-2xl overflow-hidden border border-gray-700">
                            <EmojiPicker onEmojiClick={(emojiObject) => setNewMessage(prev => prev + emojiObject.emoji)} theme="dark" />
                        </div>
                    )}
                    <HoverTooltip text={t('messagesPage.chat.emojiTooltip')}>
                        <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 rounded-full text-gray-400 hover:text-pink-400 hover:bg-gray-700 transition-colors">
                            <LucideIcons.Smile size={20} />
                        </button>
                    </HoverTooltip>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleImageSelect} 
                        className="hidden" 
                        accept="image/*" 
                    />
                    <HoverTooltip text={t('messagesPage.chat.attachTooltip')}>
                        <button type="button" onClick={() => fileInputRef.current.click()} className="p-2 rounded-full text-gray-400 hover:text-green-400 hover:bg-gray-700 transition-colors" aria-label="Attach Image">
                            <LucideIcons.Paperclip size={20} />
                        </button>
                    </HoverTooltip>
                    <HoverTooltip text={t('messagesPage.chat.starterTooltip')}>
                        <button type="button" onClick={() => handleGenerateStarter()} disabled={isGeneratingStarter} className="p-2 rounded-full text-gray-400 hover:text-purple-400 hover:bg-gray-700 transition-colors disabled:opacity-50" aria-label="Generate AI conversation starter">
                            {isGeneratingStarter ? <div className="action-spinner" /> : <LucideIcons.Sparkles size={20} />}
                        </button>
                    </HoverTooltip>
                    <HoverTooltip text={t('messagesPage.chat.giftTooltip')}>
                        <button type="button" onClick={() => setShowGiftModal(true)} className="p-2 rounded-full text-gray-400 hover:text-yellow-400 hover:bg-gray-700 transition-colors hidden sm:block" aria-label="Send a Gift of Echoes">
                            <LucideIcons.Gift size={20} />
                        </button>
                    </HoverTooltip>
                    <input 
                        type="text" 
                        value={newMessage}
                        onChange={handleTyping}
                        placeholder={t('messagesPage.chat.messagePlaceholder')}
                        className="flex-1 bg-transparent py-2 px-3 text-white placeholder-gray-500 focus:outline-none text-[15px]"
                        aria-label="Message input"
                    />
                    <button type="submit" className="p-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:from-indigo-400 hover:to-blue-500 disabled:opacity-50 disabled:from-gray-700 disabled:to-gray-700 transition-all shadow-md ml-1" disabled={!newMessage.trim() && !imageFile && !isRecording} aria-label={t('messagesPage.chat.sendLabel')}>
                        <LucideIcons.Send size={18} className="transform translate-x-[1px]" />
                    </button>
                    <HoverTooltip text={t('messagesPage.chat.recordTooltip')}>
                        <button 
                            type="button" 
                            onMouseDown={handleStartRecording} 
                            onMouseUp={handleStopRecording}
                            onTouchStart={handleStartRecording}
                            onTouchEnd={handleStopRecording}
                            className={`p-2 rounded-full text-red-400 transition-colors ${isRecording ? 'bg-red-500/50 animate-pulse' : 'hover:bg-white/10'}`}
                            aria-label="Record Voice Message"
                        >
                            <LucideIcons.Mic size={20} />
                        </button>
                    </HoverTooltip>
                </div>
            </form>
        </div>
    );
};

function MessagesPage() {
    const { t } = useTranslation();
    const { userId, db, appId, userProfiles, handlePageChange, appFunctions, setMessage } = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedChatUser, setSelectedChatUser] = useState(null);
    const [lastMessages, setLastMessages] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const currentUserProfile = userProfiles.find(p => p.id === userId);
    const unreadMessageCounts = currentUserProfile?.unreadMessageCounts || {};

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const chatPartnerId = params.get('chatPartnerId');
        if (chatPartnerId) {
            const profile = userProfiles.find(p => p.id === chatPartnerId);
            if (profile) {
                setSelectedChatUser({ id: profile.id, displayName: profile.displayName });
            }
        }
    }, [userProfiles]);

    useEffect(() => {
        if (!db || !userId) return;

        const q = query(collection(db, `artifacts/${appId}/private_chats`), where("members", "array-contains", userId));

        const unsub = onSnapshot(q, (snap) => {
            const lastMsgs = {};
            snap.forEach(doc => {
                const chatData = doc.data();
                const otherUserId = chatData.members.find(id => id !== userId);
                if (otherUserId && chatData.lastMessage) {
                    lastMsgs[otherUserId] = chatData.lastMessage;
                }
            });
            setLastMessages(lastMsgs);
        });

        return () => unsub();
    }, [userId, db, appId]);

    const allUsers = useMemo(() => {
        if (!userId || !userProfiles) return [];

        const baseUsers = userProfiles.filter(p => p.id !== userId && !p.isAI);

        const sortedUsers = [...baseUsers].sort((a, b) => {
            const aIsPinned = a.isPinned || false;
            const bIsPinned = b.isPinned || false;
            if (aIsPinned !== bIsPinned) return aIsPinned ? -1 : 1;

            const aUnread = unreadMessageCounts[a.id] > 0;
            const bUnread = unreadMessageCounts[b.id] > 0;
            if (aUnread !== bUnread) return aUnread ? -1 : 1;

            const timeA = lastMessages[a.id]?.timestamp?.toMillis() || 0;
            const timeB = lastMessages[b.id]?.timestamp?.toMillis() || 0;
            if (timeA !== timeB) return timeB - timeA;

            return (a.displayName || "").localeCompare(b.displayName || "");
        });

        if (searchTerm.trim() !== '') {
            return sortedUsers.filter(user =>
                (user.displayName || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        const existingChatPartnerIds = new Set([
            ...Object.keys(lastMessages),
            ...Object.keys(unreadMessageCounts).filter(id => unreadMessageCounts[id] > 0)
        ]);

        return sortedUsers.filter(user => existingChatPartnerIds.has(user.id));

    }, [userId, userProfiles, unreadMessageCounts, lastMessages, searchTerm]);

    useEffect(() => {
        setIsLoading(false);
    }, [allUsers]);

    const handleSelectUserForMessage = (targetId, targetName) => {
        handlePageChange('messages', { chatPartnerId: targetId });
        setSelectedChatUser({ id: targetId, displayName: targetName });
    };

    const handleBackToUsers = () => {
        handlePageChange('messages');
        setSelectedChatUser(null);
    };

    return (
        <div className="max-w-6xl mx-auto h-[calc(100vh-80px)] min-h-[400px]">
            <div className="flex h-full bg-gray-900 rounded-2xl shadow-xl border border-gray-800 overflow-hidden relative">
                <div className={`w-full md:w-1/3 lg:w-1/4 flex-shrink-0 border-r border-gray-800 transition-all duration-300 ${selectedChatUser ? 'hidden md:block' : 'block'}`}>
                    <UserListPanel
                        allUsers={allUsers}
                        selectedChatUser={selectedChatUser}
                        onSelectUser={handleSelectUserForMessage}
                        unreadMessageCounts={unreadMessageCounts}
                        isLoading={isLoading}
                        lastMessages={lastMessages}
                        t={t}
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                    />
                </div>
                <div className={`w-full md:flex-1 bg-gray-900/50 ${selectedChatUser ? 'block' : 'hidden md:flex md:flex-col md:items-center md:justify-center'}`}>
                    {selectedChatUser ? (
                        <ChatPanel selectedChatUser={selectedChatUser} onBackToUsers={handleBackToUsers} t={t} />
                    ) : (
                        <div className="text-gray-500 hidden md:flex flex-col items-center">
                            <LucideIcons.MessageSquare size={48} className="mb-4 opacity-50" />
                            <p className="text-lg font-medium">{t('messagesPage.userList.noMatches') || 'Select a user to start messaging'}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MessagesPage;
